// A heartbeat, so a live panel still looks live when nothing moved.
//
// Owner, 2026-10-05: "I also don't see it pulsing or flashing yet."
//
// ⚠️ THE FLASH WAS WORKING. IT IS JUST RARE PER TILE, AND THAT IS THE HONEST
// BEHAVIOUR. Measured over one minute on the six headline indexes: Feeder
// Steer moved 343.35 -> 343.54, Calf 475.82 -> 475.89, Cow 154.14 -> 154.16,
// Steer & Heifer 500+ 353.81 -> 353.86 — but Feeder Heifer and Slaughter Bull
// did not move at all. A rolling seven-day index has thousands of head
// underneath it, so a handful of new lots often does not reach the second
// decimal place. At a twenty-second poll, any one tile flashes maybe once a
// minute or two. Watch a single tile and you see nothing happen.
//
// ⚠️ SO THE FIX IS NOT TO FLASH HARDER. Flashing a price that did not change
// would be the page telling a small lie every twenty seconds, on the product
// whose whole claim is that its numbers can be checked. The flash stays exactly
// as honest as it is; this says the separate, also-true thing — that the panel
// is connected and was refreshed a moment ago — which is what someone watching
// actually wants to know.

import { useEffect, useState } from "react";

/** How often the "Ns ago" text re-renders. One second: it is a clock. */
const TICK_MS = 1000;

function ago(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.round(m / 60)}h ago`;
}

/**
 * `at` is the moment of the last SUCCESSFUL fetch — not the last time the
 * figures changed. Those are different, and the difference is the entire point:
 * a panel that only announced itself when a price moved would look dead on a
 * quiet market, which is exactly when a reader most wants to know the feed is
 * still running.
 */
export default function LiveBeat({ at }: { at: number | null }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (at === null) return;
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [at]);

  // ⚠️ NOTHING UNTIL THE FIRST FETCH LANDS. These pages are prerendered and
  // served until the next deploy; a "live" badge baked into the HTML would
  // claim a connection that does not exist yet and would still be claiming it
  // if the fetch failed.
  if (at === null) return null;

  // ⚠️ SAY SO WHEN IT HAS GONE QUIET. The poll is twenty seconds, so a minute
  // without one means something is wrong — a dropped connection, a failing
  // RPC — and a cheerful pulsing dot over a stale number is worse than no dot.
  const stale = now - at > 60_000;

  return (
    <span className="rbtx-beat" data-stale={stale ? "" : undefined}
          title={stale ? "The live feed has gone quiet" : "Live — updating as lots arrive"}>
      <i aria-hidden="true" />
      <b>{stale ? "Reconnecting" : "Live"}</b>
      <em>{ago(now - at)}</em>
    </span>
  );
}
